import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PrivacyPolicy = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Datenschutzerklärung</h1>
            <p className="text-gray-600">Gültig ab: 28. Juni 2025 | Version 1.1</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>DSGVO-Konform:</strong> Diese Datenschutzerklärung entspricht den Anforderungen 
                    der EU-Datenschutz-Grundverordnung (DSGVO) und informiert Sie umfassend über die 
                    Verarbeitung Ihrer personenbezogenen Daten bei uplivion.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Verantwortlicher</h2>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2">
                  <strong>Verantwortlich für die Datenverarbeitung ist:</strong>
                </p>
                <p className="mb-2">
                  <strong>Arc Rider Ventures GmbH</strong><br/>
                  Jänickendorfer Str. 64a<br/>
                  14943 Brandenburg<br/>
                  Deutschland
                </p>
                <p className="mb-2">
                  <strong>Handelsregister:</strong> Amtsgericht Berlin Charlottenburg HRB199081B<br/>
                  <strong>Geschäftsführer:</strong> Jakob Jordan
                </p>
                <p>
                  <strong>E-Mail:</strong> support@uplivion.life<br/>
                  <strong>Datenschutzbeauftragter:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Erhobene Daten</h2>
              <h3 className="text-xl font-semibold mb-3">2.1 Registrierungsdaten</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>E-Mail-Adresse (Pflichtfeld)</li>
                <li>Benutzername (Pflichtfeld)</li>
                <li>Vor- und Nachname (Pflichtfeld)</li>
                <li>Geburtsdatum (Pflichtfeld)</li>
                <li>Passwort (verschlüsselt gespeichert)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.2 Timeline-Inhalte</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Texte und Beschreibungen von Ereignissen</li>
                <li>Hochgeladene Fotos und Bilder</li>
                <li>Datum und Zeitangaben zu Ereignissen</li>
                <li>Kategorisierungen und Tags</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.3 Technische Daten</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>IP-Adresse (zeitweise für Sicherheit)</li>
                <li>Browser-Informationen</li>
                <li>Geräteinformationen</li>
                <li>Nutzungsstatistiken (anonymisiert)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Zwecke der Datenverarbeitung</h2>
              <h3 className="text-xl font-semibold mb-3">3.1 Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Bereitstellung der Timeline-Funktionen</li>
                <li>Benutzerkonto-Verwaltung</li>
                <li>Speicherung und Anzeige Ihrer Inhalte</li>
                <li>Freunde-Funktionen und Sharing</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.2 Berechtigte Interessen (Art. 6 Abs. 1 lit. f DSGVO)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Verbesserung des Service (Beta-Testing)</li>
                <li>Sicherheit und Missbrauchsprävention</li>
                <li>Technische Optimierung</li>
                <li>Kundensupport</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.3 Einwilligung (Art. 6 Abs. 1 lit. a DSGVO)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Marketing-E-Mails (optional)</li>
                <li>Erweiterte Analytics (optional)</li>
                <li>Newsletter (optional)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Technische Infrastruktur und Hosting</h2>
              <p className="mb-4">
                uplivion nutzt ausschließlich europäische Dienstleister für eine DSGVO-konforme 
                und sichere Datenverarbeitung:
              </p>

              <div className="grid md:grid-cols-1 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">🇪🇺 Vollständig Europäische Infrastruktur</h4>
                  <p className="text-sm text-green-700">
                    Alle Ihre Daten werden ausschließlich innerhalb der EU/EWR verarbeitet und gespeichert.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.1 Website-Hosting (Netlify)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Anbieter:</strong> Netlify, Inc.</p>
                <p className="mb-2"><strong>Standort:</strong> Europäische CDN-Edge-Locations (Frankfurt, London)</p>
                <p className="mb-2"><strong>Zweck:</strong> Website-Hosting und Content Delivery</p>
                <p className="mb-2"><strong>Verarbeitete Daten:</strong> IP-Adresse, Browser-Informationen, Zugriffsprotokolle</p>
                <p className="mb-2"><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)</p>
                <p className="text-sm text-gray-600">
                  Netlify stellt sicher, dass europäische Nutzer über europäische Server bedient werden. 
                  Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO ist vorhanden.
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.2 Datenbank-Services (Supabase)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Anbieter:</strong> Supabase, Inc.</p>
                <p className="mb-2"><strong>Standort:</strong> Deutschland (Frankfurt)</p>
                <p className="mb-2"><strong>Zweck:</strong> Datenbank-Hosting, Authentifizierung, Echtzeit-Services</p>
                <p className="mb-2"><strong>Verarbeitete Daten:</strong> Alle Nutzerdaten, Timeline-Inhalte, Authentifizierungsdaten</p>
                <p className="mb-2"><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
                <p className="text-sm text-gray-600">
                  Alle Datenbank-Server befinden sich in Deutschland. Supabase ist SOC 2 Type II zertifiziert 
                  und erfüllt DSGVO-Anforderungen.
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.3 E-Mail-Services (Resend)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Anbieter:</strong> Resend Technologies Limited</p>
                <p className="mb-2"><strong>Standort:</strong> Irland (Dublin)</p>
                <p className="mb-2"><strong>Zweck:</strong> Transaktionale E-Mails, Benachrichtigungen</p>
                <p className="mb-2"><strong>Verarbeitete Daten:</strong> E-Mail-Adressen, E-Mail-Inhalte, Versandstatistiken</p>
                <p className="mb-2"><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
                <p className="text-sm text-gray-600">
                  Resend operiert vollständig innerhalb der EU und erfüllt alle DSGVO-Anforderungen 
                  für E-Mail-Service-Provider.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Auftragsverarbeiter und Datenweitergabe</h2>
              <p className="mb-4">
                Ihre Daten werden nur an folgende Auftragsverarbeiter weitergegeben, 
                die alle DSGVO-konforme Auftragsverarbeitungsverträge haben:
              </p>

              <div className="space-y-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Netlify (Website-Hosting)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Europäische CDN-Infrastruktur</li>
                    <li>• ISO 27001 und SOC 2 zertifiziert</li>
                    <li>• DSGVO-konforme Datenverarbeitung</li>
                    <li>• Keine Drittland-Übertragungen für EU-Nutzer</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Supabase (Datenbank)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Server-Standort: Deutschland (Frankfurt)</li>
                    <li>• SOC 2 Type II und ISO 27001 zertifiziert</li>
                    <li>• End-to-End-Verschlüsselung</li>
                    <li>• Vollständige EU-Datenresidenz</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Resend (E-Mail-Service)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Irische Rechtsgrundlage (EU)</li>
                    <li>• DSGVO-konforme E-Mail-Zustellung</li>
                    <li>• Verschlüsselte Datenübertragung</li>
                    <li>• Automatische Löschung von E-Mail-Logs</li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Keine Weitergabe an:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Dritte zu Werbezwecken</li>
                  <li>• Nicht-EU-Dienstleister ohne Angemessenheitsbeschluss</li>
                  <li>• Social Media Plattformen</li>
                  <li>• Datenbroker oder Analytics-Anbieter</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Datensicherheit</h2>
              <p className="mb-4">
                Wir verwenden modernste Sicherheitstechnologien und -verfahren:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Verschlüsselung:</strong> TLS 1.3 für alle Datenübertragungen</li>
                <li><strong>Speicher-Verschlüsselung:</strong> AES-256 für alle gespeicherten Daten</li>
                <li><strong>Zugriffskontrolle:</strong> Mehrstufige Authentifizierung für alle Systeme</li>
                <li><strong>Monitoring:</strong> 24/7 Sicherheitsüberwachung</li>
                <li><strong>Backups:</strong> Verschlüsselte tägliche Backups in EU-Rechenzentren</li>
                <li><strong>Zertifizierungen:</strong> Alle Partner sind ISO 27001 und/oder SOC 2 zertifiziert</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Ihre Rechte</h2>
              <p className="mb-4">Sie haben folgende Rechte bezüglich Ihrer Daten:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Information über gespeicherte Daten</li>
                <li><strong>Berichtigungsrecht (Art. 16 DSGVO):</strong> Korrektur falscher Daten</li>
                <li><strong>Löschungsrecht (Art. 17 DSGVO):</strong> Löschung Ihrer Daten</li>
                <li><strong>Einschränkung (Art. 18 DSGVO):</strong> Beschränkung der Verarbeitung</li>
                <li><strong>Datenübertragbarkeit (Art. 20 DSGVO):</strong> Export Ihrer Daten</li>
                <li><strong>Widerspruchsrecht (Art. 21 DSGVO):</strong> Widerspruch gegen Verarbeitung</li>
                <li><strong>Widerruf (Art. 7 DSGVO):</strong> Widerruf erteilter Einwilligungen</li>
              </ul>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">Ausübung Ihrer Rechte</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Zur Bearbeitung Ihrer Anfragen kontaktieren Sie uns bitte unter:
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Alle Datenschutzanfragen:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Speicherdauer</h2>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Registrierungsdaten:</strong> Bis zur Löschung des Kontos</li>
                <li><strong>Timeline-Inhalte:</strong> Bis zur Löschung durch Sie oder Ihr Konto</li>
                <li><strong>Technische Logs:</strong> Maximal 30 Tage (Netlify, Supabase)</li>
                <li><strong>E-Mail-Logs:</strong> Maximal 7 Tage (Resend)</li>
                <li><strong>Backup-Daten:</strong> Maximal 90 Tage nach Löschung</li>
                <li><strong>Marketing-Einwilligungen:</strong> Bis zum Widerruf</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Cookies und Tracking</h2>
              <p className="mb-4">
                uplivion verwendet nur technisch notwendige Cookies:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Session-Cookies:</strong> Für die Anmeldung und Navigation</li>
                <li><strong>Einstellungs-Cookies:</strong> Für Ihre Präferenzen (Sprache, etc.)</li>
                <li><strong>Sicherheits-Cookies:</strong> Schutz vor Missbrauch</li>
              </ul>
              <p className="mb-4">
                <strong>Keine Tracking-Cookies</strong> von Drittanbietern ohne Ihre Einwilligung.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Internationale Datenübertragungen</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ Keine Drittland-Übertragungen</h4>
                <p className="text-sm text-green-700">
                  Alle Ihre Daten werden ausschließlich innerhalb der EU/EWR verarbeitet. 
                  Es finden keine Übertragungen in Drittländer statt.
                </p>
              </div>
              
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Deutschland:</strong> Supabase-Datenbank (Frankfurt)</li>
                <li><strong>Irland:</strong> Resend E-Mail-Service (Dublin)</li>
                <li><strong>EU-CDN:</strong> Netlify Edge-Locations (Frankfurt, London)</li>
              </ul>
              
              <p className="mb-4">
                Alle genannten Standorte unterliegen der DSGVO und bieten das gleiche 
                Datenschutzniveau wie Deutschland.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Besonderheiten der Beta-Phase</h2>
              <p className="mb-4">
                Während der Beta-Phase gelten besondere Bestimmungen:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Erweiterte Logs:</strong> Anonymisierte Debug-Informationen für max. 7 Tage</li>
                <li><strong>Feedback-Sammlung:</strong> Freiwillige Teilnahme an Verbesserungsvorschlägen</li>
                <li><strong>Performance-Monitoring:</strong> Anonymisierte Nutzungsstatistiken</li>
                <li><strong>Datenmigrationen:</strong> Rechtzeitige Information bei System-Updates</li>
              </ul>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">Beta-Phase Garantien</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Alle DSGVO-Rechte bleiben vollständig bestehen</li>
                  <li>• Keine Lockerung der Datenschutz-Standards</li>
                  <li>• Zusätzliche Transparenz über Datenverarbeitung</li>
                  <li>• Kostenlose Datenexporte jederzeit möglich</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Beschwerderecht</h2>
              <p className="mb-4">
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die 
                Verarbeitung Ihrer Daten zu beschweren:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Zuständige Behörde (Deutschland)</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Die Landesbeauftragte für den Datenschutz und für das Recht auf Akteneinsicht Brandenburg</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Stahnsdorfer Damm 77<br/>
                    14532 Kleinmachnow<br/>
                    E-Mail: poststelle@lda.brandenburg.de
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">EU-Ebene</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Sie können sich auch an die Datenschutzbehörde Ihres Wohnsitzes wenden oder 
                    eine Beschwerde bei der EU-Kommission einreichen.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Kontakt & Datenschutzbeauftragte</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Verantwortlicher</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Arc Rider Ventures GmbH</strong><br/>
                      Jänickendorfer Str. 64a<br/>
                      14943 Brandenburg<br/>
                      Deutschland
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Geschäftsführer:</strong> Jakob Jordan<br/>
                      <strong>HRB:</strong> 199081B (AG Berlin Charlottenburg)
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Datenschutz-Kontakte</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li><strong>Allgemein:</strong> support@uplivion.life</li>
                      <li><strong>Datenschutzbeauftragte:</strong> dpo@uplivion.life</li>
                      <li><strong>Löschungsanträge:</strong> delete@uplivion.life</li>
                      <li><strong>Datenexport:</strong> export@uplivion.life</li>
                      <li><strong>Beschwerden:</strong> complaints@uplivion.life</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
